import { inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";

// Core map imports
import { MapService } from "../../../viewer-components/services/internal-services/map.service";
import { Circle, Feature, Fill, OverlayManager, Stroke, Style, VectorLayer, WKT } from "@vision/core-map-api";

/**
 * Interface for tracking geofencing layer state
 */
interface GeofencingLayerState {
  layerId: string;
  layerExists: boolean;
  layerVisible: boolean;
  hasGeometry: boolean;
  wkt: string | null;
  type: "polygon" | "circle" | null;
  lastFeatureId: string | null;
}

/**
 * Interface for geofencing geometry data
 */
export interface GeofencingGeometry {
  wkt: string;
  type: "polygon" | "circle";
}

/**
 * Service for managing geofencing layer on the map
 * Handles layer creation, geometry management, and visibility control
 * Follows the same pattern as MapSearchLayerService for consistency
 */
@Injectable()
export class GeofencingLayerService {
  mapService = inject(MapService);

  private readonly LAYER_ID = "geofencing-layer";

  private readonly FEATURE_ID_PREFIX = "geofencing-feature";

  private layerState: GeofencingLayerState = {
    layerId: this.LAYER_ID,
    layerExists: false,
    layerVisible: true, // Default to visible when created
    hasGeometry: false,
    wkt: null,
    type: null,
    lastFeatureId: null,
  };

  private stateSubject = new BehaviorSubject<GeofencingLayerState>({ ...this.layerState });

  /**
   * Create or update the geofencing layer with new geometry
   * @param geometryData Geometry data containing WKT, optional GeometryCollection, and type
   */
  createOrUpdateLayer(geometryData: GeofencingGeometry): void {
    try {
      // Update state with new geometry data
      this.layerState.wkt = geometryData.wkt;
      this.layerState.type = geometryData.type;
      this.layerState.hasGeometry = true;

      // Create layer if it doesn't exist
      if (!this.layerState.layerExists) {
        this.createLayer();
      }

      // Update geometry in existing layer
      this.updateLayerGeometry(geometryData);

      // Ensure layer is visible by default
      this.layerState.layerVisible = true;

      this.emitState();
    } catch (error) {
      console.error("GeofencingLayerService: Error creating/updating layer:", error);
      this.handleLayerError("Failed to create or update geofencing layer");
    }
  }

  /**
   * Toggle the visibility of the geofencing layer
   * @returns The new visibility state
   */
  toggleVisibility(): boolean {
    if (!this.layerState.layerExists) {
      console.warn("GeofencingLayerService: Cannot toggle visibility - no layer exists");
      return false;
    }

    this.layerState.layerVisible = !this.layerState.layerVisible;
    this.updateLayerVisibility();
    this.emitState();

    return this.layerState.layerVisible;
  }

  /**
   * Remove the geofencing layer completely
   */
  removeFeatures(): void {
    if (!this.layerState.layerExists) {
      console.log("GeofencingLayerService: No layer to remove");
      return;
    }

    try {
      const layer = this.getLayer();
      // Clear existing features - remove each feature individually
      const existingFeatures = layer.getFeatures() || [];
      for (const feature of existingFeatures) {
        layer.removeFeature(feature);
      }
      this.layerState.hasGeometry = false;
      this.layerState.wkt = null;
      this.layerState.type = null;
      this.layerState.lastFeatureId = null;
      this.emitState();
    } catch (error) {
      console.error("GeofencingLayerService: Error removing layer:", error);
      // Reset state even if removal failed to prevent inconsistent state
      this.resetLayerState();
      this.emitState();
    }
  }

  /**
   * Get current layer state
   */
  getState(): GeofencingLayerState {
    return { ...this.layerState };
  }

  /**
   * Create a new empty geofencing layer
   */
  private createLayer(): void {
    try {
      const layer = new VectorLayer({
        id: this.layerState.layerId,
        features: [],
      });

      this.getOverlayManager().addLayer(layer);
      this.layerState.layerExists = true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update geometry in the existing layer
   */
  private updateLayerGeometry(geometryData: GeofencingGeometry): void {
    const layer = this.getLayer();
    if (!layer) {
      console.error("GeofencingLayerService: Layer not found for geometry update");
      return;
    }

    try {
      // Clear existing features - remove each feature individually
      const existingFeatures = layer.getFeatures() || [];
      for (const feature of existingFeatures) {
        layer.removeFeature(feature);
      }

      // Create new feature from geometry
      const feature = this.createFeatureFromGeometry(geometryData);
      if (feature) {
        layer.addFeature(feature);
        this.layerState.lastFeatureId = feature.get("id");
      }
    } catch (error) {
      console.error("GeofencingLayerService: Error updating geometry:", error);
      throw error;
    }
  }

  /**
   * Create a styled feature from geometry data
   */
  private createFeatureFromGeometry(geometryData: GeofencingGeometry): Feature | null {
    try {
      const wktReader = new WKT();
      let geometry = wktReader.readGeometry(geometryData.wkt);

      // Create feature
      const featureId = `${this.FEATURE_ID_PREFIX}-${Date.now()}`;
      const feature = new Feature(geometry);
      feature.set("id", featureId);
      feature.set("type", "geofencing");
      feature.set("geometryType", geometryData.type);

      // Apply styling
      const style = this.createGeofencingStyle();
      feature.setStyle(style);

      return feature;
    } catch (error) {
      console.error("GeofencingLayerService: Error creating feature:", error);
      return null;
    }
  }

  /**
   * Create styling for geofencing features
   */
  private createGeofencingStyle(): Style {
    // Geofencing style: Semi-transparent fill with distinctive border
    const fillColor = "transparent";
    const strokeColor = "white";

    return new Style({
      image: new Circle({
        radius: 2,
        fill: new Fill({
          color: fillColor,
        }),
        stroke: new Stroke({
          color: strokeColor,
          width: 1,
        }),
      }),
      fill: new Fill({ color: fillColor }),
      stroke: new Stroke({
        color: strokeColor,
        width: 2,
        lineDash: [5, 5], // Dashed line for better distinction
      }),
    });
  }

  /**
   * Update layer visibility without affecting geometry
   * Instead of hiding the layer, we'll manage features to simulate visibility
   */
  private updateLayerVisibility(): void {
    const layer = this.getLayer();
    if (!layer) {
      console.error("GeofencingLayerService: Layer not found for visibility update");
      return;
    }
    layer.setVisible(this.layerState.layerVisible);
  }

  /**
   * Get the geofencing layer
   */
  private getLayer(): VectorLayer | null {
    try {
      return this.getOverlayManager().getLayerById(this.layerState.layerId) as VectorLayer;
    } catch (error) {
      console.error("GeofencingLayerService: Error getting layer:", error);
      return null;
    }
  }

  /**
   * Get the overlay manager
   */
  private getOverlayManager(): OverlayManager {
    return this.mapService.getOverlayManager();
  }

  /**
   * Reset layer state to initial values
   */
  private resetLayerState(): void {
    this.layerState = {
      layerId: this.LAYER_ID,
      layerExists: false,
      layerVisible: true,
      hasGeometry: false,
      wkt: null,
      type: null,
      lastFeatureId: null,
    };
  }

  /**
   * Handle layer operation errors
   */
  private handleLayerError(message: string): void {
    console.error(`GeofencingLayerService: ${message}`);
    // TODO: Consider implementing user notification for critical errors
    // For now, log error and ensure state consistency
  }

  /**
   * Emit current state to subscribers
   */
  private emitState(): void {
    this.stateSubject.next({ ...this.layerState });
  }
}
